import React, { FC, useContext, useRef, useState } from 'react';
import {
	Section,
	Tab,
	TabPayload,
} from '@Pages/ClassRooms/Types/ClassroomTabs.types';
import {
	Draggable,
	DraggableProvidedDragHandleProps,
	Droppable,
} from 'react-beautiful-dnd';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	IconButton,
	TextField,
} from '@mui/material';
import EdIcon from '@Components/UI/Utilities/EdIcon/EdIcon';
import { FlexItem, FlexLayout, Spacer } from '@Styled/utilities';
import { grey } from '@mui/material/colors';
import {
	useController,
	useFieldArray,
	useForm,
	useFormContext,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { TAB_SCHEMA } from '@Pages/ClassRooms/Schema/ClassroomCurriculum.schema';
import EdFab from '@Components/UI/Utilities/EdFab/EdFab';
import { FabButtonLabel, SectionDroppable } from '../../Curriculum.styled';
import { CURRICULUM_FORM } from '../../../../../../Schema/ClassroomCurriculum.schema';
import { ClassroomTabsRequester } from '@Pages/ClassRooms/Services/ClassroomTabs/ClassroomTabs.req';
import EdAnimatedIcon from '@Components/UI/Utilities/EdAnimatedIcon/EdAnimatedIcon';
import {
	CurriculumContext,
	CurriculumSectionContext,
} from '../../Context/Curriculum.context';
import { ClassroomSectionsRequester } from '@Pages/ClassRooms/Services/ClassroomTabs/ClassroomSections.req';
import SectionAccordion from '../SectionAccordion/SectionAccordion';
import DragButton from '@Components/UI/Buttons/EdDragButton/EdDragButton';
import EdCollapseButton from '@Components/UI/Buttons/EdCollapseButton/EdCollapseButton';
import { lighten } from 'polished';
import { DevTool } from '@hookform/devtools';
import { useTypedSelector } from '@Features/store';
import styled from 'styled-components';

import EdConfirmModal from '@Components/UI/Modals/EdConfirmModal/EdConfirmModal';
import { useSnackbar } from '@Providers/useSnackbar';
type Props = {
	tab: Tab;
	dragHandlers?: DraggableProvidedDragHandleProps | null;
	index: number;
	isDragging?: boolean;
};
const TabAccordion: FC<Props> = ({ tab, dragHandlers, index, isDragging }) => {
	const { control, setValue: setFormValue } = useFormContext<CURRICULUM_FORM>();
	const [opened, setOpened] = useState(false);
	const { updateTab, deleteTab } = useContext(CurriculumContext);
	const { control: TabControl, reset } = useForm<TabPayload>({
		resolver: yupResolver(TAB_SCHEMA),
		defaultValues: { ...tab },
		mode: 'all',
	});
	const auth = useTypedSelector((state) => state.auth);
	const hasEditPermission =
		auth.user.type === 'super' || auth.permissions.has('u-cur-cl');
	const { field, fieldState } = useController({
		control: TabControl,
		name: `name`,
	});
	const { fields, append, update, remove } = useFieldArray({
		control,
		name: `tabs.${index}.sections`,
		keyName: 'sectionfId',
	});
	const [modalOpened, setModalOpened] = useState(false);
	// One section create at a time. A focused MUI Fab is a <button>, so the
	// browser fires a click for every keydown repeat while Enter is held — on
	// the standalone curriculum a stuck key once wrote 5,498 rows in three
	// minutes. `sectionPending` disables the control (dropping it out of the
	// focus order, which is what breaks the repeat chain); the ref covers the
	// tick before that state lands.
	const addingSection = useRef(false);
	const [sectionPending, setSectionPending] = useState(false);
	const { displaySnackbar } = useSnackbar();

	const onConfirm = async () => {
		try {
			// name only: the whole tab carried its pre-save order back and the
			// response (no sections) replaced the node, emptying the tab
			await ClassroomTabsRequester.getInstance().update({ id: tab.id, name: field.value } as any);
			setFormValue(`tabs.${index}.name`, field.value);
			reset({ ...tab, name: field.value }, { keepDirty: false });
			displaySnackbar('success', 'Edited Tab Successfully');
		} catch (error: any) {
			displaySnackbar('error', error?.response?.data?.message ?? "Couldn't rename the tab");
		}
	};

	const onDelete = async () => {
		setModalOpened(true);
	};
	const canDelete =
		auth.user.type === 'super' || auth.permissions.has('d-cur-cl');
	const onConfirmDelete = async (close: boolean, confirm?: boolean) => {
		setModalOpened(close);
		if (!confirm || !tab.id) return;
		try {
			await ClassroomTabsRequester.getInstance().delete(tab.id);
			displaySnackbar('success', `${tab.name} deleted successfully`);
			deleteTab && deleteTab(index);
		} catch (error: any) {
			displaySnackbar('error', error?.response?.data?.message ?? "Couldn't delete the tab");
		}
	};

	const onAddSection = async () => {
		if (addingSection.current) return;
		addingSection.current = true;
		setSectionPending(true);
		try {
			const {
				data: { data },
				status,
			} = await ClassroomSectionsRequester.getInstance().create({
				classroom_id: tab.classroom_id,
				classroom_tab_id: tab.id ?? 0,
				name: `Section ${fields.length + 1}`,
				order: fields.length + 1,
				courses: [],
			});
			if (status === 200) {
				displaySnackbar('success', `${data.name} created successfully`);
				append(data as Section);
			}
		} finally {
			addingSection.current = false;
			setSectionPending(false);
		}
	};

	const onRemove = (index: number) => {
		remove(index);
	};

	return (
		<div>
			<EdConfirmModal
				handleClose={onConfirmDelete}
				text={`Are you sure to delete ${tab.name}?`}
				open={modalOpened}
			/>
			<StyledAccordion
				isDragging={isDragging}
				expanded={opened}
				TransitionProps={{ unmountOnExit: true }}
			>
				<AccordionSummary disableRipple={true} disableTouchRipple={true}>
					<FlexLayout width="100%">
						<FlexItem flex="1">
							<EdCollapseButton
								collapsed={!opened}
								onClick={() => setOpened(!opened)}
							/>
							<FlexLayout alignItems="center">
								{dragHandlers && <DragButton dragHandlers={dragHandlers} />}
								<TabTitle>Tab {index + 1}</TabTitle>
								<TextField
									{...field}
									error={fieldState.invalid}
									size="small"
									disabled={!hasEditPermission}
								/>

								<Spacer mx="1rem" />
							</FlexLayout>
						</FlexItem>
						<FlexItem>
							{canDelete && (
								<IconButton onClick={onDelete}>
									<EdIcon>delete</EdIcon>
								</IconButton>
							)}
							{fieldState.isDirty && hasEditPermission && (
								<IconButton onClick={onConfirm} color="warning">
									<EdAnimatedIcon animation="shake">
										<EdIcon>check_circle_outline</EdIcon>
									</EdAnimatedIcon>
								</IconButton>
							)}
						</FlexItem>
					</FlexLayout>
				</AccordionSummary>
				<AccordionDetails>
					{(auth.user.type === 'super' || auth.permissions.has('c-cur-cl')) && (
						<FlexLayout alignItems="center">
							<EdFab
								onClick={onAddSection}
								disabled={sectionPending}
								size="small"
								color="primary"
							>
								<EdIcon fontSize="medium">add</EdIcon>
							</EdFab>
							<Spacer mx={'0.5rem'} />
							<FabButtonLabel>Add Section</FabButtonLabel>
						</FlexLayout>
					)}
					<Droppable
						type="SECTIONS"
						key={`TAB-${index}-DROP-AREA`}
						droppableId={`TAB-${index}`}
					>
						{(
							{ droppableProps, innerRef, placeholder },
							{ isDraggingOver }
						) => {
							return (
								<SectionDroppable
									isDraggingOver={isDraggingOver}
									{...droppableProps}
									ref={innerRef}
								>
										{fields.map((section, sIndex) => {
											return (
												<Draggable
													draggableId={`SECTION-${
														section.id ?? section.sectionfId
													}`}
													index={sIndex}
													// key={`SECTION-${section.id ?? section.sectionfId}`}
													key={`${section.sectionfId}`}
												>
													{(
														{ draggableProps, innerRef, dragHandleProps },
														{ isDragging }
													) => {
														return (
															<div ref={innerRef} {...draggableProps}>
																<CurriculumSectionContext.Provider
																	key={`SECTION-CONTEXT-${sIndex}`}
																	value={{
																		tabIndex: index,
																		appendSection: () => {},
																		updateSection: update,
																		removeSection: onRemove,
																		dragHandlers: dragHandleProps,
																		isDragging,
																		index: sIndex,
																	}}
																>
																	<SectionAccordion
																		control={control}
																		section={section}
																	/>
																</CurriculumSectionContext.Provider>
															</div>
														);
													}}
												</Draggable>
											);
										})}
									{placeholder}
								</SectionDroppable>
							);
						}}
					</Droppable>
				</AccordionDetails>
			</StyledAccordion>
		</div>
	);
};

export default TabAccordion;

const StyledAccordion = styled(Accordion)<{ isDragging?: boolean }>`
	margin: 2rem 0;
	background-color: ${grey['100']};

	background-color: ${(props) =>
		props.isDragging
			? lighten(0.25, props.theme.palette.purple?.main ?? '')
			: ''};

	.MuiAccordionSummary-root.Mui-focusVisible {
		background-color: ${grey['100']};
	}

	&.MuiAccordionDetails-root:first-of-type {
		padding: 2.5rem 1.75rem 3.75rem 3.563rem;
	}

	&.Mui-expanded:last-of-type {
		margin: 2rem 0;
	}
`;

const TabTitle = styled.label`
	font-size: 0.875rem;
	margin: 0 3.75rem 0 1rem;
`;
